import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, Pencil, Play, Plus, Search, Trash2 } from "lucide-react";

import { Button, TournamentStatusBadge, useConfirm } from "@/ui";
import { formatEventDate, formatPrice } from "@/lib/format";
import { noAutofill } from "@/lib/noAutofill";
import {
  useCancelTournament,
  useDeleteTournament,
  useStartInternalTournament,
  useTournaments,
} from "@/features/queries";

export function AdminTournamentsPage() {
  const navigate = useNavigate();
  const { data: tournaments = [], isLoading } = useTournaments();
  const start = useStartInternalTournament();
  const cancel = useCancelTournament();
  const del = useDeleteTournament();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tournaments;
    return tournaments.filter((t) => {
      const creator = (t.creator_username ?? "").toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        t.spot_name.toLowerCase().includes(q) ||
        creator.includes(q)
      );
    });
  }, [search, tournaments]);

  return (
    <div>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 className="h1">Турниры</h1>
        <Button leading={<Plus size={16} />} onClick={() => navigate("/admin/tournaments/new")}>
          Создать
        </Button>
      </header>

      {tournaments.length > 0 && (
        <SearchBar value={search} onChange={setSearch} />
      )}

      {isLoading ? (
        <p className="body">Загрузка…</p>
      ) : tournaments.length === 0 ? (
        <EmptyState onAction={() => navigate("/admin/tournaments/new")} />
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: "32px 16px",
            textAlign: "center",
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
            color: "var(--fg2)",
          }}
        >
          Ничего не нашли.
        </div>
      ) : (
        <div
          className="sp-admin-table-wrap"
          style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)" }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-body)" }}>
            <thead className="sp-admin-table-head">
              <tr style={{ background: "var(--bg-secondary)" }}>
                <Th>Название</Th>
                <Th>Когда</Th>
                <Th>Место</Th>
                <Th>Создатель</Th>
                <Th>Участников</Th>
                <Th>Взнос</Th>
                <Th>Статус</Th>
                <Th>Действия</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  style={{ borderTop: "1px solid var(--separator)", cursor: "pointer" }}
                  onClick={() => navigate(`/admin/tournaments/${t.id}`)}
                >
                  <Td><strong>{t.name}</strong></Td>
                  <Td>{formatEventDate(t.date_time)}</Td>
                  <Td>{t.spot_name}</Td>
                  <Td>{t.creator_username ? `@${t.creator_username}` : "—"}</Td>
                  <Td>{t.registered_count}/{t.max_participants}</Td>
                  <Td>{formatPrice(t.entry_fee)}</Td>
                  <Td><TournamentStatusBadge status={t.status} /></Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                      {(t.status === "draft" || t.status === "registration") && (
                        <IconAction
                          title="Редактировать"
                          icon={<Pencil size={14} />}
                          onClick={() => navigate(`/admin/tournaments/${t.id}/edit`)}
                        />
                      )}
                      {t.status === "registration" && (
                        <IconAction
                          title="Стартовать турнир"
                          icon={<Play size={14} />}
                          disabled={start.isPending || start.variables === t.id}
                          onClick={() => start.mutate(t.id)}
                        />
                      )}
                      {(t.status === "registration" || t.status === "full") && (
                        <IconAction
                          title="Отменить"
                          icon={<Ban size={14} />}
                          disabled={cancel.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: "Отменить турнир?",
                              description: `«${t.name}» получит статус «отменён», записи игроков будут сняты.`,
                              confirmLabel: "Отменить турнир",
                              cancelLabel: "Назад",
                              tone: "danger",
                            });
                            if (ok) cancel.mutate(t.id);
                          }}
                        />
                      )}
                      <IconAction
                        title="Удалить"
                        icon={<Trash2 size={14} />}
                        danger
                        disabled={del.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Удалить турнир?",
                            description: `«${t.name}» и вся его сетка будут удалены навсегда.`,
                            confirmLabel: "Удалить",
                            tone: "danger",
                          });
                          if (ok) del.mutate(t.id);
                        }}
                      />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SearchBar({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "var(--bg-section)",
        borderRadius: "var(--r-md)",
        padding: "8px 12px",
        marginBottom: 12,
      }}
    >
      <Search size={16} color="var(--fg3)" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Поиск: название, место или @создатель"
        {...noAutofill}
        style={{
          flex: 1,
          border: 0,
          outline: 0,
          background: "transparent",
          fontFamily: "var(--font-body)",
          fontSize: 14,
          color: "var(--fg1)",
          minWidth: 0,
        }}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "10px 12px",
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "var(--fg3)",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "12px", fontSize: 14, verticalAlign: "middle" }}>{children}</td>;
}

function IconAction({
  icon,
  title,
  onClick,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: "var(--r-md)",
        border: 0,
        background: "var(--bg-secondary)",
        color: danger ? "var(--fg-danger)" : "var(--fg1)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {icon}
    </button>
  );
}

function EmptyState({ onAction }: { onAction: () => void }) {
  return (
    <div
      style={{
        padding: "64px 16px",
        textAlign: "center",
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
      }}
    >
      <h2 className="h2">Турниров ещё нет</h2>
      <p className="body" style={{ color: "var(--fg2)", marginTop: 8 }}>
        Создай первый — он появится в ленте /events.
      </p>
      <div style={{ marginTop: 16 }}>
        <Button onClick={onAction}>Создать турнир</Button>
      </div>
    </div>
  );
}
